import { FragmentComponent } from '@/types/graphql'
import { DSFlex, DSSpacer, DSText } from '@zoe/ds-web'
import React, { useContext } from 'react'
import { processTypographyMarkdown } from '../DesignSystem/Typography'
import { TrackingContext } from '@/context/trackingContext'
import { trackCtaClick } from '@/lib/gtm/gtmV2'
import { DSGridBlock } from '@/components/ds/Grid/Grid'

export type AppDownloadSectionRecord = {
  __typename: 'AppDownloadSectionRecord'
  id: string
  title: string
  description?: string
  appStoreDownload?: boolean
  googlePlayDownload?: boolean
  dataTestId?: string
  appStoreUrl?: string
  googlePlayUrl?: string
}

const fragment = `
  fragment AppDownloadBlockFragment on AppDownloadSectionRecord {
    __typename
    id
    title
    description
    appStoreDownload
    appStoreUrl
    googlePlayDownload
    googlePlayUrl
    dataTestId
  }
`

export const AppDownloadBlock: FragmentComponent<{}, AppDownloadSectionRecord> = ({ record }) => {
  const { section } = useContext(TrackingContext)
  return (
    <DSGridBlock
      dataTestId={record?.dataTestId}
      padded
      breakConfig={{
        s: { colSpan: 2 },
        m: { colSpan: 6 },
        l: { colSpan: 4, offset: 4 },
        xl: { colSpan: 4, offset: 4 },
      }}
    >
      <DSSpacer direction="vertical" size={48} />
      {record.title && (
        <DSText as="h5" variant="fluid-heading-500alt" weight="semiBold" align="center">
          {processTypographyMarkdown({ content: record.title, variant: 'fluid-heading-500alt' })}
        </DSText>
      )}
      <DSSpacer direction="vertical" size={8} />
      {record.description && (
        <DSText as="p" variant="fluid-paragraph-300" align="center" weight="light">
          {processTypographyMarkdown({ content: record.description, variant: 'fluid-paragraph-300' })}
        </DSText>
      )}
      <DSSpacer direction="vertical" size={16} />
      <DSFlex direction="row" gap={16} alignItems="center" justifyContent="center">
        {record.appStoreDownload && (
          <a
            href={record.appStoreUrl || 'https://apps.apple.com/us/app/zoe-insights/id1471632228'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCtaClick(section || 'cms', 'App Store Download')}
          >
            <img
              src="/assets/img/app/AppStore.png"
              alt="Download on the App Store"
              style={{ display: 'block', width: '166px' }}
            />
          </a>
        )}
        {record.googlePlayDownload && (
          <a
            href={record.googlePlayUrl || 'https://play.google.com/store/apps/details?id=com.joinzoe.results'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCtaClick(section || 'cms', 'Google Play Download')}
          >
            <img
              src="/assets/img/app/PlayStore.png"
              alt="Get it on Google Play"
              style={{ display: 'block', width: '166px' }}
            />
          </a>
        )}
      </DSFlex>
      <DSSpacer direction="vertical" size={72} />
    </DSGridBlock>
  )
}

AppDownloadBlock.fragment = fragment
AppDownloadBlock.recordName = 'AppDownloadSectionRecord'
